import { cn } from '@/lib/utils';
import { PlannedTransactionPaymentStatus } from '@/features/public/bookingSummary/types/PlannedTransactionPaymentStatus.ts';
import StatusDot from '@/components/ui/StatusDot.tsx';
import { InstallmentVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import {
  getInstallmentStatusLabel,
  INSTALLMENT_STATUS_TONE,
} from '@/features/dashboard/bookingChanges/types/BillViewModel.utils.ts';
import { formatDate } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import RowActionsMenu, {
  RowActionsMenuItem,
} from '@/components/ui/RowActionsMenu.tsx';
import { installmentsGrid, stackedCell } from './paymentTableLayout';

// Rata anulowana przez paczkę nie jest już do zapłaty — oznaczanie jej wpłatą
// dopisałoby pieniądze do rezerwacji, która ich nie oczekuje.
const canMarkAsPaid = (status: PlannedTransactionPaymentStatus): boolean =>
  status !== PlannedTransactionPaymentStatus.PAID &&
  status !== PlannedTransactionPaymentStatus.CANCELLED;

const InstallmentRow = ({
  installment,
  onMarkAsPaid,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  installment: InstallmentVm;
  onMarkAsPaid?: (installment: InstallmentVm) => void;
}) => {
  const menuItems: RowActionsMenuItem[] =
    onMarkAsPaid && canMarkAsPaid(installment.status)
      ? [
          {
            label: 'Oznacz jako opłacone',
            onSelect: () => onMarkAsPaid(installment),
          },
        ]
      : [];

  return (
    <div
      role="row"
      className={cn(
        installmentsGrid,
        'items-center border-b border-dashed border-neutral-100',
        className,
      )}
      {...props}
    >
      <div
        role="cell"
        className={cn(
          'py-3.5 text-[15px] font-semibold text-ink',
          stackedCell.topLeft,
        )}
      >
        {installment.name}
      </div>
      <div
        role="cell"
        className={cn(
          'py-3.5 text-[15px] text-neutral-800',
          stackedCell.bottomLeft,
        )}
      >
        {formatDate(installment.dueDate)}
      </div>
      <div
        role="cell"
        className={cn(
          'py-3.5 text-right text-[15px] font-semibold tabular-nums text-ink',
          stackedCell.topRight,
        )}
      >
        <MoneyAmountList values={installment.amount} />
      </div>
      <div
        role="cell"
        className={cn('flex justify-end py-3.5', stackedCell.bottomRight)}
      >
        <StatusDot
          className="text-[15px]"
          tone={INSTALLMENT_STATUS_TONE[installment.status]}
          label={getInstallmentStatusLabel(installment.status)}
        />
      </div>
      <div role="cell" className={cn('py-2.5', stackedCell.actions)}>
        <RowActionsMenu label={installment.name} items={menuItems} />
      </div>
    </div>
  );
};

export default InstallmentRow;
